'use client';

import { KeyboardEvent, ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import styles from './SearchSelect.module.scss';
export interface SearchSelectOption {
  /** Stable key. */
  id: string;
  /** Shown in the list and in the field once picked. */
  label: string;
  /** Saved to the form; defaults to `label` (e.g. English name for a localized label). */
  value?: string;
  /** Grey text after the label (e.g. the country of a city). */
  secondary?: string;
}
export interface SearchSelectProps {
  /** The form value. Free text is saved as typed; a pick saves its `value`. */
  value: string;
  onChange: (value: string) => void;
  /** Returns matches for the query (best first). */
  search: (query: string) => Promise<SearchSelectOption[]> | SearchSelectOption[];
  placeholder?: string;
  /** Shown until `minChars` are typed. */
  hintText: string;
  loadingText: string;
  noResultsText: string;
  /** "Other" row: clears the field to type something unlisted. */
  otherLabel: string;
  /** Characters needed before searching; 0 lists everything on open. */
  minChars?: number;
  /** Results shown (the rest are dropped); Infinity for the full list. */
  maxVisible?: number;
  /** Scroll the menu past this height (for long, full lists). */
  maxMenuHeight?: number;
  /** Read-only, no menu (a prefilled value that mustn't change). */
  locked?: boolean;
  invalid?: boolean;
  onBlur?: () => void;
  leadingIcon?: ReactNode;
  /** Class for the <input> (the caller's form input style). */
  inputClassName?: string;
  lockedInputClassName?: string;
  'aria-labelledby'?: string;
}
const SEARCH_DEBOUNCE_MS = 250;

/** Bold the part of a label that matches what was typed. */
function highlightMatch(label: string, query: string): ReactNode {
  const q = query.trim();
  const at = q ? label.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return label;
  return <>
            {label.slice(0, at)}
            <strong>{label.slice(at, at + q.length)}</strong>
            {label.slice(at + q.length)}
        </>;
}

/**
 * Type-to-search field with a suggestion menu: after `minChars` it shows the
 * top `maxVisible` matches (typed part in bold, optional grey secondary
 * text) plus an "Other" row, all without scrolling. ↑↓ move, Enter picks,
 * Esc closes just the menu. Whatever is typed is kept as the value, so an
 * unlisted entry still submits; "Other" clears the field to type one.
 */
const SearchSelect = ({
  value,
  onChange,
  search,
  placeholder,
  hintText,
  loadingText,
  noResultsText,
  otherLabel,
  minChars = 2,
  maxVisible = 4,
  maxMenuHeight,
  locked = false,
  invalid = false,
  onBlur,
  leadingIcon,
  inputClassName,
  lockedInputClassName,
  'aria-labelledby': ariaLabelledBy
}: SearchSelectProps) => {
  const id = useId();
  const listId = `${id}-list`;
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef(search);
  searchRef.current = search;
  // Last value this field emitted — a different incoming value is an
  // outside change (prefill/reset) and replaces the text.
  const emitted = useRef(value);
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<SearchSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value;
      setQuery(value);
    }
  }, [value]);
  const emit = (next: string) => {
    emitted.current = next;
    onChange(next);
  };
  const ready = query.trim().length >= minChars;
  useEffect(() => {
    if (!open || !ready) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const found = await searchRef.current(query.trim());
        if (cancelled) return;
        // Same label + secondary is the same entry.
        const seen = new Set<string>();
        setResults(found.filter(o => {
          const key = `${o.label.toLowerCase()}|${(o.secondary || '').toLowerCase()}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }));
        setActiveIndex(-1);
      } catch (err) {
        console.error(err);
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Nothing typed yet (a full list on open): no reason to wait.
    }, query.trim() ? SEARCH_DEBOUNCE_MS : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, ready, query]);

  // Close on a press anywhere outside.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  // Rows the keyboard walks: the top results, then "Other" last — only
  // once something is typed (on an empty field it has nothing to add).
  const visible = ready ? results.slice(0, maxVisible) : [];
  const showOther = ready && query.trim().length > 0;
  const rowCount = visible.length + (showOther ? 1 : 0);
  useLayoutEffect(() => {
    if (!open || activeIndex < 0) return;
    listRef.current?.querySelector(`[data-row="${activeIndex}"]`)?.scrollIntoView({
      block: 'nearest'
    });
  }, [open, activeIndex]);
  const pick = (option: SearchSelectOption) => {
    setQuery(option.label);
    emit(option.value ?? option.label);
    setOpen(false);
    setActiveIndex(-1);
  };

  // Empty the field to type something unlisted; suggestions keep coming.
  const pickOther = () => {
    setQuery('');
    emit('');
    setActiveIndex(-1);
    inputRef.current?.focus();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (locked) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setOpen(true);
        if (rowCount > 0) {
          setActiveIndex(i => Math.min(i + 1, rowCount - 1));
        }
        return;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
        return;
      case 'Enter':
        if (!open || activeIndex < 0) return;
        e.preventDefault();
        if (activeIndex < visible.length) pick(visible[activeIndex]);else if (showOther) pickOther();
        return;
      case 'Escape':
        if (!open) return;
        // Close just the menu, not a surrounding modal.
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        setActiveIndex(-1);
        return;
      case 'Tab':
        setOpen(false);
        return;
    }
  };
  const rowId = (i: number) => `${id}-opt-${i}`;
  return <div className={styles.root} ref={rootRef} data-sentry-component="SearchSelect" data-sentry-source-file="SearchSelect.tsx">
            {leadingIcon && <span className={styles.leading}>{leadingIcon}</span>}
            <input ref={inputRef} className={`${inputClassName ?? styles.input} ${leadingIcon ? styles.withLeading : ''} ${locked ? lockedInputClassName ?? '' : ''} ${invalid ? styles.invalid : ''}`} role="combobox" aria-expanded={locked ? false : open} aria-controls={open ? listId : undefined} aria-activedescendant={open && activeIndex >= 0 ? rowId(activeIndex) : undefined} aria-autocomplete="list" aria-invalid={invalid || undefined} aria-labelledby={ariaLabelledBy} autoComplete="off" readOnly={locked} placeholder={placeholder} value={query} onFocus={e => {
      if (locked) return;
      setOpen(true);
      // Make it easy to replace a prefilled value.
      e.currentTarget.select();
    }} onChange={e => {
      if (locked) return;
      setQuery(e.target.value);
      emit(e.target.value);
      setOpen(true);
      setActiveIndex(-1);
    }} onKeyDown={onKeyDown} onBlur={onBlur} />
            {!locked && <ChevronDown size={18} className={styles.chevron} aria-hidden />}

            {open && !locked && <ul id={listId} ref={listRef} className={`${styles.menu} ${maxMenuHeight ? styles.menuScroll : ''}`} style={maxMenuHeight ? {
      maxHeight: maxMenuHeight
    } : undefined} role="listbox"
    // Keep focus on the input so the menu isn't torn down mid-click.
    onMouseDown={e => e.preventDefault()}>
                    {!ready ? <li className={styles.empty}>{hintText}</li> : loading && results.length === 0 ? <li className={styles.empty}>{loadingText}</li> : results.length === 0 ? <li className={styles.empty}>{noResultsText}</li> : visible.map((option, i) => <li key={option.id} id={rowId(i)} data-row={i} role="option" aria-selected={i === activeIndex} className={`${styles.option} ${i === activeIndex ? styles.optionActive : ''}`} onMouseEnter={() => setActiveIndex(i)} onClick={() => pick(option)}>
                                <span className={styles.label}>
                                    {highlightMatch(option.label, query)}
                                </span>
                                {option.secondary && <span className={styles.secondary}>
                                        {option.secondary}
                                    </span>}
                            </li>)}
                    {showOther && <li id={rowId(visible.length)} data-row={visible.length} role="option" aria-selected={activeIndex === visible.length} className={`${styles.option} ${styles.optionOther} ${activeIndex === visible.length ? styles.optionActive : ''}`} onMouseEnter={() => setActiveIndex(visible.length)} onClick={pickOther}>
                            {otherLabel}
                        </li>}
                </ul>}
        </div>;
};
export default SearchSelect;